import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Alert
} from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { CarImport, CarImportSearchParams } from '../api/apiClient';
import CarDetailsModal from './CarDetailsModal';
import { useAverageTax } from '../hooks/useCarImports';

interface CarImportsTableProps {
  data: CarImport[];
  loading: boolean;
  error: Error | null;
  page: number;
  rowsPerPage: number;
  totalCount: number;
  searchParams: CarImportSearchParams;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
}

export const CarImportsTable: React.FC<CarImportsTableProps> = ({
  data,
  loading,
  error,
  page,
  rowsPerPage,
  totalCount,
  searchParams,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [selectedCar, setSelectedCar] = useState<CarImport | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { data: averageTaxData } = useAverageTax(searchParams);
  if (error) return <Alert severity="error">Error: {error.message}</Alert>;
  if (loading && !data.length) return <CircularProgress />;
  if (!loading && !data.length) return <Typography>No data found</Typography>;

  return (
    <Paper elevation={2}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Make</TableCell>
              <TableCell>Model</TableCell>
              <TableCell>Year</TableCell>
              <TableCell>Fuel Type</TableCell>
              <TableCell>Mileage (km)</TableCell>
              <TableCell>Drive Type</TableCell>
              <TableCell>Date of Import</TableCell>
              <TableCell>Declared Value</TableCell>
              <TableCell>Tax Amount</TableCell>
              <TableCell>View</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Average Tax Summary Row */}
            {averageTaxData && (
              <TableRow sx={{ backgroundColor: '#f5f5f5', fontWeight: 'bold' }}>
                <TableCell colSpan={7} sx={{ fontWeight: 'bold' }}>
                  Average Tax for Current Search
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>-</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  €{averageTaxData.average_tax?.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '0.00'}
                </TableCell>
                <TableCell>-</TableCell>
              </TableRow>
            )}
            {data.map((row, index) => (
              <TableRow 
                key={row.id}
                sx={{ 
                  backgroundColor: index % 2 === 0 ? '#fafafa' : 'white',
                  '&:hover': { backgroundColor: '#e3f2fd' },
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setSelectedCar(row);
                  setModalOpen(true);
                }}
              >
                <TableCell sx={{ fontWeight: 'medium' }}>{row.make}</TableCell>
                <TableCell>{row.model}</TableCell>
                <TableCell>
                  <Chip 
                    label={row.year || 'N/A'} 
                    size="small" 
                    variant="outlined"
                    color={row.year && row.year > 2015 ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Chip 
                    label={row.fuel_type || 'Unknown'} 
                    size="small"
                    color={row.fuel_type?.toLowerCase().includes('electric') ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell>{row.mileage_km?.toLocaleString() || '-'} km</TableCell>
                <TableCell>{row.drive_type || '-'}</TableCell>
                <TableCell>{row.decision_date ? new Date(row.decision_date).toLocaleDateString('fi-FI') : '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'medium' }}>
                  €{row.declared_value?.toLocaleString('fi-FI', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  €{row.calculated_tax?.toLocaleString('fi-FI', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || '-'}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Tooltip title="View Details">
                    <IconButton 
                      size="small" 
                      onClick={() => {
                        setSelectedCar(row);
                        setModalOpen(true);
                      }}
                      sx={{ color: 'primary.main' }}
                    >
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
      />
      
      <CarDetailsModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedCar(null);
        }}
        car={selectedCar}
      />
    </Paper>
  );
};
